import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, MessageCircle, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { CONTACT, waLink, WA_DEFAULT_MSG, scrollToSection } from "@/lib/site";

const ANCHOR_LINKS = [
  { id: "services", label: "Services" },
  { id: "coverage", label: "Coverage" },
  { id: "why", label: "Why NGCONDE" },
  { id: "faq", label: "FAQ" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(id), 500);
    } else {
      scrollToSection(id);
    }
  };

  const goHome = (e) => {
    e.preventDefault();
    setOpen(false);
    if (pathname !== "/") navigate("/");
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-white/10 bg-[#080C14]/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-20 lg:px-8">
        <a href="/" onClick={goHome} data-testid="nav-link-home" aria-label="NGCONDE Courier Service home">
          <Logo />
        </a>

        <div className="hidden items-center gap-7 lg:flex">
          {ANCHOR_LINKS.map((l) => (
            <a
              key={l.id}
              href={`/#${l.id}`}
              onClick={go(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="font-mono text-xs font-medium uppercase tracking-[0.18em] text-slate-300 transition-colors hover:text-[#FF6B00]"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href={waLink(WA_DEFAULT_MSG)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-whatsapp"
            className="flex items-center gap-2 rounded-full border border-[#25D366]/50 px-4 py-2 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-[#25D366] transition-all hover:bg-[#25D366] hover:text-[#080C14]"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            {CONTACT.whatsappDisplay}
          </a>
          <a
            href="/contact"
            data-testid="nav-cta-partner"
            className="rounded-full bg-[#FF6B00] px-5 py-2 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-[#080C14] transition-all hover:bg-[#E05E00] hover:shadow-[0_0_28px_rgba(255,107,0,0.4)] active:scale-95"
          >
            Become a Partner
          </a>
        </div>

        <button
          className="rounded-md border border-white/15 p-2 text-white lg:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-b border-white/10 bg-[#080C14]/97 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="space-y-1 px-5 py-4">
              {ANCHOR_LINKS.map((l) => (
                <a
                  key={l.id}
                  href={`/#${l.id}`}
                  onClick={go(l.id)}
                  className="block rounded-lg px-3 py-3 font-display text-lg font-bold uppercase tracking-tight text-white hover:bg-white/5"
                >
                  {l.label}
                </a>
              ))}
              <div className="mt-3 flex flex-col gap-2 border-t border-white/10 pt-4">
                <a
                  href={waLink(WA_DEFAULT_MSG)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-nav-whatsapp"
                  className="flex items-center justify-center gap-2 rounded-full border border-[#25D366]/50 px-4 py-3 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-[#25D366]"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp {CONTACT.whatsappDisplay}
                </a>
                <a
                  href={`tel:${CONTACT.callNumber}`}
                  data-testid="mobile-nav-call"
                  className="flex items-center justify-center gap-2 rounded-full border border-white/20 px-4 py-3 font-mono text-xs font-semibold uppercase tracking-[0.14em] text-white"
                >
                  <Phone className="h-4 w-4" /> {CONTACT.callDisplay}
                </a>
                <a
                  href="/contact"
                  data-testid="mobile-nav-partner"
                  className="rounded-full bg-[#FF6B00] px-4 py-3 text-center font-mono text-xs font-semibold uppercase tracking-[0.14em] text-[#080C14]"
                >
                  Become a Partner
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
